import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowRight,
  BarChart3,
  FileText,
  Scale,
  TrendingUp,
  Building2,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Colors for charts
const CONTRACT_COLORS = {
  draft: '#94a3b8',      // slate
  review: '#fbbf24',     // amber
  approved: '#22c55e',   // green
  signed: '#8b5cf6',     // purple
};

const CASE_COLORS = {
  new: '#94a3b8',           // slate
  filed: '#3b82f6',         // blue
  hearings: '#f97316',      // orange
  final_judgment: '#22c55e', // green
  execution: '#8b5cf6',      // purple
};

const COMPANY_COLORS = ['#E94B7B', '#3b82f6', '#22c55e', '#f97316', '#8b5cf6'];

export default function Analytics({ user }) {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [isMainCompany, setIsMainCompany] = useState(false);
  const [isDawrBunyan, setIsDawrBunyan] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [stats, setStats] = useState({});
  const [contracts, setContracts] = useState([]);
  const [cases, setCases] = useState([]);
  const [rentalContracts, setRentalContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDateModal, setShowDateModal] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Set company based on URL param
  useEffect(() => {
    if (companies.length > 0 && companyId) {
      const company = companies.find(c => c.id === companyId);
      if (company) {
        setCurrentCompany(company);
        setIsMainCompany(company.is_main === true);
        setIsDawrBunyan(company.name_ar && company.name_ar.includes('دور وبنيان'));
        // If main company (IFG), show all companies, otherwise show only this company
        if (company.is_main) {
          setSelectedCompany('all');
        } else {
          setSelectedCompany(companyId);
        }
      }
    }
  }, [companies, companyId]);

  useEffect(() => {
    fetchData();
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('فشل تحميل الشركات');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCompany !== 'all' ? { company_id: selectedCompany } : {};
      
      const [statsRes, contractsRes, casesRes, rentalRes, paymentsRes, executionsRes, agreementsRes, commitmentsRes] = await Promise.all([
        axios.get(`${API}/stats`, { params }),
        axios.get(`${API}/contracts`, { params }),
        axios.get(`${API}/cases`, { params }),
        axios.get(`${API}/rental-contracts`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/executions`, { params }),
        axios.get(`${API}/agreements`, { params }),
        axios.get(`${API}/commitments`, { params }),
      ]);

      setStats(statsRes.data);
      setContracts(contractsRes.data);
      setCases(casesRes.data);
      setRentalContracts(rentalRes.data);
      setPayments(paymentsRes.data);
      setExecutions(executionsRes.data);
      setAgreements(agreementsRes.data);
      setCommitments(commitmentsRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (user?.role === 'legal_manager') {
      navigate('/companies');
    } else {
      navigate('/executive');
    }
  };

  // Prepare contract status data for pie chart
  const contractStatusData = [
    { name: 'مسودة', value: stats.contracts?.draft || 0, color: CONTRACT_COLORS.draft },
    { name: 'قيد المراجعة', value: stats.contracts?.review || 0, color: CONTRACT_COLORS.review },
    { name: 'معتمد', value: stats.contracts?.approved || 0, color: CONTRACT_COLORS.approved },
    { name: 'موقّع', value: stats.contracts?.signed || 0, color: CONTRACT_COLORS.signed },
  ].filter(item => item.value > 0);

  // Prepare case status data for pie chart
  const caseStatusData = [
    { name: 'جديدة', value: stats.cases?.new || 0, color: CASE_COLORS.new },
    { name: 'مرفوعة', value: stats.cases?.filed || 0, color: CASE_COLORS.filed },
    { name: 'جلسات', value: stats.cases?.hearings || 0, color: CASE_COLORS.hearings },
    { name: 'حكم نهائي', value: stats.cases?.final_judgment || 0, color: CASE_COLORS.final_judgment },
    { name: 'تنفيذ', value: stats.cases?.execution || 0, color: CASE_COLORS.execution },
  ].filter(item => item.value > 0);

  // Prepare rental payment status data for pie chart
  const paidPayments = payments.filter(p => p.payment_status === 'paid').length;
  const unpaidPayments = payments.filter(p => p.payment_status === 'unpaid').length;
  const rentalPaymentData = [
    { name: 'مدفوعة', value: paidPayments, color: '#22c55e' },
    { name: 'غير مدفوعة', value: unpaidPayments, color: '#ef4444' },
  ].filter(item => item.value > 0);

  // Prepare execution status data from new executions collection
  const executionFiled = executions.filter(e => e.execution_status === 'filed').length;
  const executionInProgress = executions.filter(e => e.execution_status === 'in_progress').length;
  const executionCompleted = executions.filter(e => e.execution_status === 'completed').length;
  const executionStatusData = [
    { name: 'تم الرفع', value: executionFiled, color: '#3b82f6' },
    { name: 'تحت الإجراء', value: executionInProgress, color: '#f97316' },
    { name: 'منتهي', value: executionCompleted, color: '#22c55e' },
  ].filter(item => item.value > 0);

  // Calculate total execution amounts
  const totalExecutionAmount = executions.reduce((sum, e) => sum + (e.payment_amount || 0), 0);
  const completedExecutionAmount = executions
    .filter(e => e.execution_status === 'completed')
    .reduce((sum, e) => sum + (e.payment_amount || 0), 0);
  const inProgressExecutionAmount = executions
    .filter(e => e.execution_status === 'in_progress')
    .reduce((sum, e) => sum + (e.payment_amount || 0), 0);

  // Prepare agreements status data for pie chart (IFG only)
  const agreementStatusData = [
    { name: 'مسودة', value: agreements.filter(a => a.status === 'draft').length, color: CONTRACT_COLORS.draft },
    { name: 'قيد المراجعة', value: agreements.filter(a => a.status === 'review').length, color: CONTRACT_COLORS.review },
    { name: 'معتمدة', value: agreements.filter(a => a.status === 'approved').length, color: CONTRACT_COLORS.approved },
    { name: 'مُفعّلة', value: agreements.filter(a => a.status === 'signed').length, color: CONTRACT_COLORS.signed },
  ].filter(item => item.value > 0);

  // Prepare commitments status data for pie chart (IFG only)
  const commitmentStatusData = [
    { name: 'مسودة', value: commitments.filter(c => c.status === 'draft').length, color: CONTRACT_COLORS.draft },
    { name: 'قيد المراجعة', value: commitments.filter(c => c.status === 'review').length, color: CONTRACT_COLORS.review },
    { name: 'معتمد', value: commitments.filter(c => c.status === 'approved').length, color: CONTRACT_COLORS.approved },
    { name: 'موقّع', value: commitments.filter(c => c.status === 'signed').length, color: CONTRACT_COLORS.signed },
  ].filter(item => item.value > 0);

  // Prepare contracts by company data for bar chart
  const contractsByCompany = companies.map((company, index) => {
    const count = contracts.filter(c => c.company_id === company.id).length;
    return {
      name: company.name_ar,
      عقود: count,
      color: COMPANY_COLORS[index % COMPANY_COLORS.length],
    };
  });

  // Prepare cases by company data for bar chart
  const casesByCompany = companies.map((company, index) => {
    const count = cases.filter(c => c.company_id === company.id).length;
    return {
      name: company.name_ar,
      قضايا: count,
      color: COMPANY_COLORS[index % COMPANY_COLORS.length],
    };
  });

  // Get selected company info
  const getSelectedCompanyInfo = () => {
    if (selectedCompany === 'all') {
      return { name: 'مجموعة IFG', logo: null };
    }
    const company = companies.find(c => c.id === selectedCompany);
    return company ? { name: company.name_ar, logo: company.logo_url } : { name: 'مجموعة IFG', logo: null };
  };

  // Export to Excel
  const exportToExcel = () => {
    const companyInfo = getSelectedCompanyInfo();
    
    // Contracts sheet data
    const contractsData = contracts.map(c => ({
      'عنوان العقد': c.title,
      'نوع العقد': c.contract_type,
      'الطرف الثاني': c.party_name,
      'الحالة': c.status === 'draft' ? 'مسودة' : c.status === 'review' ? 'قيد المراجعة' : c.status === 'approved' ? 'معتمد' : 'موقّع',
      'القيمة': c.value || 0,
      'تاريخ الإنشاء': new Date(c.created_at).toLocaleDateString('ar-SA'),
      'الشركة': companies.find(comp => comp.id === c.company_id)?.name_ar || '-',
    }));

    // Cases sheet data
    const casesData = cases.map(c => ({
      'رقم القضية': c.case_number,
      'عنوان القضية': c.title,
      'نوع القضية': c.case_type,
      'المدعي': c.plaintiff,
      'المدعى عليه': c.defendant,
      'الحالة': c.status === 'new' ? 'جديدة' : c.status === 'filed' ? 'مرفوعة' : c.status === 'hearings' ? 'جلسات' : c.status === 'final_judgment' ? 'حكم نهائي' : 'تنفيذ',
      'تاريخ الإنشاء': new Date(c.created_at).toLocaleDateString('ar-SA'),
      'الشركة': companies.find(comp => comp.id === c.company_id)?.name_ar || '-',
    }));

    // Stats summary
    const summaryData = [
      { 'البيان': 'إجمالي العقود', 'العدد': stats.contracts?.total || 0 },
      { 'البيان': 'عقود مسودة', 'العدد': stats.contracts?.draft || 0 },
      { 'البيان': 'عقود قيد المراجعة', 'العدد': stats.contracts?.review || 0 },
      { 'البيان': 'عقود معتمدة', 'العدد': stats.contracts?.approved || 0 },
      { 'البيان': 'عقود موقّعة', 'العدد': stats.contracts?.signed || 0 },
      { 'البيان': '', 'العدد': '' },
      { 'البيان': 'إجمالي القضايا', 'العدد': stats.cases?.total || 0 },
      { 'البيان': 'قضايا جديدة', 'العدد': stats.cases?.new || 0 },
      { 'البيان': 'قضايا مرفوعة', 'العدد': stats.cases?.filed || 0 },
      { 'البيان': 'قضايا في الجلسات', 'العدد': stats.cases?.hearings || 0 },
      { 'البيان': 'أحكام نهائية', 'العدد': stats.cases?.final_judgment || 0 },
      { 'البيان': 'قضايا في التنفيذ', 'العدد': stats.cases?.execution || 0 },
    ];

    // Add Agreements and Commitments stats for IFG
    if (isMainCompany) {
      summaryData.push(
        { 'البيان': '', 'العدد': '' },
        { 'البيان': 'إجمالي الاتفاقيات', 'العدد': agreements.length },
        { 'البيان': 'اتفاقيات مسودة', 'العدد': agreements.filter(a => a.status === 'draft').length },
        { 'البيان': 'اتفاقيات قيد المراجعة', 'العدد': agreements.filter(a => a.status === 'review').length },
        { 'البيان': 'اتفاقيات معتمدة', 'العدد': agreements.filter(a => a.status === 'approved').length },
        { 'البيان': 'اتفاقيات مُفعّلة', 'العدد': agreements.filter(a => a.status === 'signed').length },
        { 'البيان': '', 'العدد': '' },
        { 'البيان': 'إجمالي إقرارات الموظفين', 'العدد': commitments.length },
        { 'البيان': 'إقرارات مسودة', 'العدد': commitments.filter(c => c.status === 'draft').length },
        { 'البيان': 'إقرارات قيد المراجعة', 'العدد': commitments.filter(c => c.status === 'review').length },
        { 'البيان': 'إقرارات معتمدة', 'العدد': commitments.filter(c => c.status === 'approved').length },
        { 'البيان': 'إقرارات موقّعة', 'العدد': commitments.filter(c => c.status === 'signed').length },
      );
    }

    // Agreements sheet data (for IFG)
    const agreementsData = agreements.map(a => ({
      'رقم الاتفاقية': a.agreement_number,
      'العنوان': a.title,
      'الطرف الثاني': a.party_name,
      'الحالة': a.status === 'draft' ? 'مسودة' : a.status === 'review' ? 'قيد المراجعة' : a.status === 'approved' ? 'معتمدة' : 'مُفعّلة',
      'التاريخ': a.agreement_date ? new Date(a.agreement_date).toLocaleDateString('ar-SA') : '-',
      'تاريخ الإنشاء': new Date(a.created_at).toLocaleDateString('ar-SA'),
    }));

    // Commitments sheet data (for IFG)
    const commitmentsData = commitments.map(c => ({
      'رقم الإقرار': c.commitment_number,
      'اسم الموظف': c.employee_name,
      'رقم الموظف': c.employee_id || '-',
      'نوع الإقرار': c.commitment_type,
      'الحالة': c.status === 'draft' ? 'مسودة' : c.status === 'review' ? 'قيد المراجعة' : c.status === 'approved' ? 'معتمد' : 'موقّع',
      'التاريخ': c.commitment_date ? new Date(c.commitment_date).toLocaleDateString('ar-SA') : '-',
      'تاريخ الإنشاء': new Date(c.created_at).toLocaleDateString('ar-SA'),
    }));

    const wb = XLSX.utils.book_new();
    
    const ws1 = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'ملخص الإحصائيات');
    
    const ws2 = XLSX.utils.json_to_sheet(contractsData);
    XLSX.utils.book_append_sheet(wb, ws2, 'العقود');
    
    const ws3 = XLSX.utils.json_to_sheet(casesData);
    XLSX.utils.book_append_sheet(wb, ws3, 'القضايا');

    // Add Agreements and Commitments sheets for IFG
    if (isMainCompany && agreementsData.length > 0) {
      const ws4 = XLSX.utils.json_to_sheet(agreementsData);
      XLSX.utils.book_append_sheet(wb, ws4, 'الاتفاقيات');
    }

    if (isMainCompany && commitmentsData.length > 0) {
      const ws5 = XLSX.utils.json_to_sheet(commitmentsData);
      XLSX.utils.book_append_sheet(wb, ws5, 'إقرارات الموظفين');
    }

    const fileName = `تقرير_${companyInfo.name}_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('تم تصدير التقرير إلى Excel بنجاح');
  };

  // Export to PDF - using backend API
  const exportToPDF = async () => {
    try {
      let params = selectedCompany !== 'all' ? `?company_id=${selectedCompany}` : '';
      
      // Add date range if specified
      if (fromDate) {
        params += params ? `&from_date=${fromDate}` : `?from_date=${fromDate}`;
      }
      if (toDate) {
        params += params ? `&to_date=${toDate}` : `?to_date=${toDate}`;
      }
      
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/export/pdf${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('فشل تصدير PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const companyInfo = getSelectedCompanyInfo();
      a.download = `تقرير_${companyInfo.name}_${new Date().toLocaleDateString('ar-SA')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('تم تصدير التقرير إلى PDF بنجاح');
      setShowDateModal(false);
    } catch (error) {
      toast.error('فشل تصدير PDF');
      console.error(error);
    }
  };

  // Custom tooltip for bar charts
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-900">{label}</p>
          <p className="text-sm text-slate-600">
            {payload[0]?.name}: <span className="font-bold">{payload[0]?.value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie charts
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-900">{payload[0]?.name}</p>
          <p className="text-sm text-slate-600">
            العدد: <span className="font-bold">{payload[0]?.value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowRight size={20} className="ml-2" />
            العودة
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
                <BarChart3 className="text-[#E94B7B]" size={40} />
                التقارير
              </h1>
              <p className="text-slate-600">
                {currentCompany ? (
                  isMainCompany ? 'تقارير شاملة لجميع الشركات' : `تقارير ${currentCompany.name_ar}`
                ) : 'تقارير شاملة للعقود والقضايا'}
              </p>
            </div>
            {/* Show company selector only for main company (IFG) */}
            {isMainCompany && (
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-64 h-11 bg-white border-slate-200" data-testid="company-filter">
                  <SelectValue placeholder="اختر الشركة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الشركات</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name_ar}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {/* Export Buttons */}
          <div className="flex gap-3 mt-4">
            <Button
              onClick={exportToExcel}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet size={18} className="ml-2" />
              تصدير Excel
            </Button>
            <Button
              onClick={() => setShowDateModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="export-pdf-btn"
            >
              <Download size={18} className="ml-2" />
              تصدير PDF
            </Button>
          </div>

          {/* Date Range Modal for PDF Export */}
          <Dialog open={showDateModal} onOpenChange={setShowDateModal}>
            <DialogContent className="sm:max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-right">تصدير تقرير PDF</DialogTitle>
                <DialogDescription className="text-right">
                  حدد الفترة الزمنية للتقرير (اختياري)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-right block">من تاريخ</Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="text-right"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-right block">إلى تاريخ</Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="text-right"
                  />
                </div>
                <p className="text-sm text-slate-500 text-right">
                  * اتركها فارغة لتصدير كافة البيانات
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFromDate('');
                    setToDate('');
                    setShowDateModal(false);
                  }}
                >
                  إلغاء
                </Button>
                <Button
                  onClick={exportToPDF}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Download size={18} className="ml-2" />
                  تصدير
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Stats - Different for دور وبنيان */}
        {isDawrBunyan ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm mb-1">إجمالي التنفيذات</p>
                    <p className="text-4xl font-bold">{executions.length}</p>
                  </div>
                  <Scale size={48} className="text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100 text-sm mb-1">تحت الإجراء</p>
                    <p className="text-4xl font-bold">{executionInProgress}</p>
                  </div>
                  <TrendingUp size={48} className="text-yellow-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">منتهية</p>
                    <p className="text-4xl font-bold">{executionCompleted}</p>
                  </div>
                  <FileText size={48} className="text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm mb-1">إجمالي المبالغ</p>
                    <p className="text-2xl font-bold">{totalExecutionAmount.toLocaleString('ar-SA')} ر.س</p>
                  </div>
                  <Building2 size={48} className="text-purple-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm mb-1">إجمالي العقود</p>
                    <p className="text-4xl font-bold">{stats.contracts?.total || 0}</p>
                  </div>
                  <FileText size={48} className="text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#E94B7B] to-pink-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-pink-100 text-sm mb-1">إجمالي القضايا</p>
                    <p className="text-4xl font-bold">{stats.cases?.total || 0}</p>
                  </div>
                  <Scale size={48} className="text-pink-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">العقود الموقّعة</p>
                    <p className="text-4xl font-bold">{stats.contracts?.signed || 0}</p>
                  </div>
                  <TrendingUp size={48} className="text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm mb-1">قيد المراجعة</p>
                    <p className="text-4xl font-bold">{stats.contracts?.review || 0}</p>
                  </div>
                  <Building2 size={48} className="text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Grid - Only for non-دور وبنيان companies */}
        {!isDawrBunyan && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Contract Status Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-right flex items-center gap-2">
                <FileText className="text-blue-600" size={24} />
                توزيع العقود حسب الحالة
              </CardTitle>
              <CardDescription className="text-right">
                نسبة كل حالة من إجمالي العقود
              </CardDescription>
            </CardHeader>
            <CardContent>
              {contractStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={contractStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {contractStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p>لا توجد بيانات</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Case Status Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-right flex items-center gap-2">
                <Scale className="text-[#E94B7B]" size={24} />
                توزيع القضايا حسب الحالة
              </CardTitle>
              <CardDescription className="text-right">
                نسبة كل حالة من إجمالي القضايا
              </CardDescription>
            </CardHeader>
            <CardContent>
              {caseStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={caseStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {caseStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <Scale size={48} className="mx-auto mb-4 opacity-50" />
                    <p>لا توجد بيانات</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        {/* Agreements & Commitments Section - Only for IFG (main company) */}
        {isMainCompany && !isDawrBunyan && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Agreements Status Pie Chart */}
            <Card className="border-2 border-indigo-100">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-transparent">
                <CardTitle className="text-right flex items-center gap-2">
                  <FileText className="text-indigo-600" size={24} />
                  توزيع الاتفاقيات حسب الحالة
                </CardTitle>
                <CardDescription className="text-right">
                  إجمالي الاتفاقيات: {agreements.length}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {agreementStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={agreementStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {agreementStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <FileText size={48} className="mx-auto mb-4 opacity-50" />
                      <p>لا توجد اتفاقيات</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commitments Status Pie Chart */}
            <Card className="border-2 border-teal-100">
              <CardHeader className="bg-gradient-to-r from-teal-50 to-transparent">
                <CardTitle className="text-right flex items-center gap-2">
                  <Scale className="text-teal-600" size={24} />
                  توزيع إقرارات الموظفين حسب الحالة
                </CardTitle>
                <CardDescription className="text-right">
                  إجمالي الإقرارات: {commitments.length}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {commitmentStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={commitmentStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {commitmentStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <Scale size={48} className="mx-auto mb-4 opacity-50" />
                      <p>لا توجد إقرارات</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Agreements & Commitments Detailed Stats - Only for IFG */}
        {isMainCompany && !isDawrBunyan && (agreements.length > 0 || commitments.length > 0) && (
          <Card className="mb-8 border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-right">ملخص الاتفاقيات والإقرارات</CardTitle>
              <CardDescription className="text-right">
                جدول تفصيلي لحالات الاتفاقيات وإقرارات الموظفين
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Agreement Stats */}
                <div>
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <FileText className="text-indigo-600" size={20} />
                    إحصائيات الاتفاقيات
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg">
                      <span className="text-slate-600">مسودة</span>
                      <span className="font-bold text-slate-900">{agreements.filter(a => a.status === 'draft').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span className="text-amber-700">قيد المراجعة</span>
                      <span className="font-bold text-amber-700">{agreements.filter(a => a.status === 'review').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <span className="text-emerald-700">معتمدة</span>
                      <span className="font-bold text-emerald-700">{agreements.filter(a => a.status === 'approved').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-700">مُفعّلة</span>
                      <span className="font-bold text-purple-700">{agreements.filter(a => a.status === 'signed').length}</span>
                    </div>
                  </div>
                </div>

                {/* Commitment Stats */}
                <div>
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Scale className="text-teal-600" size={20} />
                    إحصائيات إقرارات الموظفين
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg">
                      <span className="text-slate-600">مسودة</span>
                      <span className="font-bold text-slate-900">{commitments.filter(c => c.status === 'draft').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span className="text-amber-700">قيد المراجعة</span>
                      <span className="font-bold text-amber-700">{commitments.filter(c => c.status === 'review').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <span className="text-emerald-700">معتمد</span>
                      <span className="font-bold text-emerald-700">{commitments.filter(c => c.status === 'approved').length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-700">موقّع</span>
                      <span className="font-bold text-purple-700">{commitments.filter(c => c.status === 'signed').length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agreements Table */}
              {agreements.length > 0 && (
                <div className="mt-8">
                  <h4 className="font-bold text-slate-800 mb-4">قائمة الاتفاقيات</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-indigo-50">
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">رقم الاتفاقية</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">العنوان</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">الطرف الثاني</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">التاريخ</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agreements.slice(0, 10).map((agr) => (
                          <tr key={agr.id} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-sm">{agr.agreement_number}</td>
                            <td className="py-2 px-3 text-sm">{agr.title}</td>
                            <td className="py-2 px-3 text-sm">{agr.party_name}</td>
                            <td className="py-2 px-3 text-sm">{agr.agreement_date ? new Date(agr.agreement_date).toLocaleDateString('ar-SA') : '-'}</td>
                            <td className="py-2 px-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                agr.status === 'signed' ? 'bg-purple-100 text-purple-700' :
                                agr.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                agr.status === 'review' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {agr.status === 'signed' ? 'مُفعّلة' :
                                 agr.status === 'approved' ? 'معتمدة' :
                                 agr.status === 'review' ? 'قيد المراجعة' : 'مسودة'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {agreements.length > 10 && (
                      <p className="text-center text-slate-500 text-sm mt-2">
                        عرض أول 10 سجلات من أصل {agreements.length}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Commitments Table */}
              {commitments.length > 0 && (
                <div className="mt-8">
                  <h4 className="font-bold text-slate-800 mb-4">قائمة إقرارات الموظفين</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-teal-50">
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">رقم الإقرار</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">اسم الموظف</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">نوع الإقرار</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">التاريخ</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commitments.slice(0, 10).map((cmt) => (
                          <tr key={cmt.id} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-sm">{cmt.commitment_number}</td>
                            <td className="py-2 px-3 text-sm">{cmt.employee_name}</td>
                            <td className="py-2 px-3 text-sm">{cmt.commitment_type}</td>
                            <td className="py-2 px-3 text-sm">{cmt.commitment_date ? new Date(cmt.commitment_date).toLocaleDateString('ar-SA') : '-'}</td>
                            <td className="py-2 px-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                cmt.status === 'signed' ? 'bg-purple-100 text-purple-700' :
                                cmt.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                cmt.status === 'review' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {cmt.status === 'signed' ? 'موقّع' :
                                 cmt.status === 'approved' ? 'معتمد' :
                                 cmt.status === 'review' ? 'قيد المراجعة' : 'مسودة'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {commitments.length > 10 && (
                      <p className="text-center text-slate-500 text-sm mt-2">
                        عرض أول 10 سجلات من أصل {commitments.length}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bar Charts - Only show when viewing all companies and not دور وبنيان */}
        {selectedCompany === 'all' && !isDawrBunyan && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contracts by Company */}
            <Card>
              <CardHeader>
                <CardTitle className="text-right flex items-center gap-2">
                  <Building2 className="text-blue-600" size={24} />
                  العقود حسب الشركة
                </CardTitle>
                <CardDescription className="text-right">
                  عدد العقود لكل شركة
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={contractsByCompany} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="عقود" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cases by Company */}
            <Card>
              <CardHeader>
                <CardTitle className="text-right flex items-center gap-2">
                  <Building2 className="text-[#E94B7B]" size={24} />
                  القضايا حسب الشركة
                </CardTitle>
                <CardDescription className="text-right">
                  عدد القضايا لكل شركة
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={casesByCompany} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="قضايا" fill="#E94B7B" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Stats Table - Only for non-دور وبنيان */}
        {!isDawrBunyan && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-right">ملخص تفصيلي</CardTitle>
            <CardDescription className="text-right">
              جدول تفصيلي لحالات العقود والقضايا
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contract Stats */}
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FileText className="text-blue-600" size={20} />
                  إحصائيات العقود
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg">
                    <span className="text-slate-600">مسودة</span>
                    <span className="font-bold text-slate-900">{stats.contracts?.draft || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-amber-700">قيد المراجعة</span>
                    <span className="font-bold text-amber-700">{stats.contracts?.review || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-emerald-700">معتمد</span>
                    <span className="font-bold text-emerald-700">{stats.contracts?.approved || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-purple-700">موقّع</span>
                    <span className="font-bold text-purple-700">{stats.contracts?.signed || 0}</span>
                  </div>
                </div>
              </div>

              {/* Case Stats */}
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Scale className="text-[#E94B7B]" size={20} />
                  إحصائيات القضايا
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg">
                    <span className="text-slate-600">جديدة</span>
                    <span className="font-bold text-slate-900">{stats.cases?.new || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-700">مرفوعة</span>
                    <span className="font-bold text-blue-700">{stats.cases?.filed || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-orange-700">جلسات</span>
                    <span className="font-bold text-orange-700">{stats.cases?.hearings || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-emerald-700">حكم نهائي</span>
                    <span className="font-bold text-emerald-700">{stats.cases?.final_judgment || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-purple-700">تنفيذ</span>
                    <span className="font-bold text-purple-700">{stats.cases?.execution || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Rental Contracts Stats Section - Only for دور وبنيان */}
        {isDawrBunyan && (
          <>
            <Card className="mt-6 border-2 border-[#E94B7B]/20">
              <CardHeader className="bg-gradient-to-r from-[#E94B7B]/5 to-transparent">
                <CardTitle className="text-right flex items-center gap-2">
                  <Scale className="text-[#E94B7B]" size={24} />
                  إحصائيات التنفيذات (دور وبنيان)
                </CardTitle>
                <CardDescription className="text-right">
                  ملخص طلبات التنفيذ وحالاتها
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-blue-600 text-sm mb-1">إجمالي التنفيذات</p>
                    <p className="text-3xl font-bold text-blue-700">{executions.length}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <p className="text-yellow-600 text-sm mb-1">تحت الإجراء</p>
                    <p className="text-3xl font-bold text-yellow-700">{executionInProgress}</p>
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-lg text-center">
                    <p className="text-emerald-600 text-sm mb-1">منتهية</p>
                    <p className="text-3xl font-bold text-emerald-700">{executionCompleted}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <p className="text-purple-600 text-sm mb-1">إجمالي المبالغ</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {totalExecutionAmount.toLocaleString('ar-SA')} ر.س
                    </p>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Execution Status Chart */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-bold text-slate-800 mb-4 text-center">توزيع حالات التنفيذ</h4>
                    {executionStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={executionStatusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {executionStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-slate-500">
                        <p>لا توجد طلبات تنفيذ</p>
                      </div>
                    )}
                  </div>

                  {/* Execution Amounts Summary */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-bold text-slate-800 mb-4 text-center">ملخص المبالغ</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-slate-600">مبالغ تحت الإجراء</span>
                        <span className="font-bold text-yellow-600">
                          {inProgressExecutionAmount.toLocaleString('ar-SA')} ر.س
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-slate-600">مبالغ منتهية</span>
                        <span className="font-bold text-emerald-600">
                          {completedExecutionAmount.toLocaleString('ar-SA')} ر.س
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-white rounded-lg border border-purple-200">
                        <span className="text-slate-700 font-medium">الإجمالي</span>
                        <span className="font-bold text-purple-700 text-lg">
                          {totalExecutionAmount.toLocaleString('ar-SA')} ر.س
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Executions Table */}
                {executions.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-bold text-slate-800 mb-4">قائمة التنفيذات</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">رقم العقد</th>
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">رقم الدفعة</th>
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">المبلغ</th>
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">رقم طلب التنفيذ</th>
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">تاريخ الرفع</th>
                            <th className="text-right py-2 px-3 text-sm font-semibold text-slate-600">الحالة</th>
                          </tr>
                        </thead>
                        <tbody>
                          {executions.slice(0, 10).map((exec) => (
                            <tr key={exec.id} className="border-b border-slate-100">
                              <td className="py-2 px-3 text-sm">{exec.contract_number}</td>
                              <td className="py-2 px-3 text-sm">{exec.payment_number}</td>
                              <td className="py-2 px-3 text-sm">{exec.payment_amount?.toLocaleString('ar-SA')} ر.س</td>
                              <td className="py-2 px-3 text-sm">{exec.execution_request_number}</td>
                              <td className="py-2 px-3 text-sm">{exec.filing_date ? new Date(exec.filing_date).toLocaleDateString('ar-SA') : '-'}</td>
                              <td className="py-2 px-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  exec.execution_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  exec.execution_status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {exec.execution_status === 'completed' ? 'منتهي' :
                                   exec.execution_status === 'in_progress' ? 'تحت الإجراء' : 'تم الرفع'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {executions.length > 10 && (
                        <p className="text-center text-slate-500 text-sm mt-2">
                          عرض أول 10 سجلات من أصل {executions.length}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
